/** One label: its localization key and the English fallback. */
export type LabelEntry = readonly [key: string, fallback: string];

/** A label table an element declares next to its type: entries, nested by group. */
export type LabelTable = { readonly [name: string]: LabelEntry | LabelTable };

/** The strings an element renders, shaped like its table. */
export type Labels<T extends LabelTable> = { readonly [K in keyof T]: T[K] extends LabelEntry ? string : T[K] extends LabelTable ? Labels<T[K]> : never };

/** The table resolved through the localization service (`t(key, fallback)`). */
export function localizeLabels<T extends LabelTable>(table: T, t: (key: string, fallback: string) => string): Labels<T> {
  const labels: Record<string, unknown> = {};
  for (const [name, entry] of Object.entries(table)) {
    labels[name] = Array.isArray(entry) ? t(entry[0], entry[1]) : localizeLabels(entry as LabelTable, t);
  }
  return labels as Labels<T>;
}

/** The English fallbacks alone: an element's labels before the factory localizes them (tests, standalone renders). */
export function fallbackLabels<T extends LabelTable>(table: T): Labels<T> {
  return localizeLabels(table, (_key, fallback) => fallback);
}
